/* @flow */

import React from 'react';
import validations from './validations';

import { Input } from 'dibs-elements/exports/Input';

type ValidatedInputProps = {
    dataTn?: string,
    disabled?: boolean,
    errorDataTn?: string,
    hasAnimatedPlaceholder?: boolean,
    incorrectFieldErrorMessage?: string,
    isRequiredField?: boolean,
    label?: string,
    maskForPrivacy?: boolean,
    onBlur?: (e: SyntheticInputEvent<*>) => mixed,
    onChange?: (e: SyntheticInputEvent<*>) => mixed,
    onEmptyField?: () => mixed,
    onError?: () => mixed,
    onSuccess?: () => mixed,
    placeholder?: string,
    requiredFieldErrorMessage: string,
    showSuccessIcon?: boolean,
    validateOnMount?: boolean,
    validationFunction?: (value: string) => boolean,
    validationType: 'email' | 'emails',
    value?: string,
};

type ValidatedInputState = {
    errorMessage: string,
    hasValidText: boolean,
    value: string,
};

class ValidatedInput extends React.Component<ValidatedInputProps, ValidatedInputState> {
    static defaultProps = {
        allowMultipleEmails: false,
        dataTn: 'validated-text-input',
        disabled: false,
        errorDataTn: 'validated-text-input-error',
        hasAnimatedPlaceholder: true,
        incorrectFieldErrorMessage: '',
        isRequiredField: false,
        label: '',
        placeholder: '',
        requiredFieldErrorMessage: 'Required Field',
        showSuccessIcon: true,
        validateOnMount: false,
        value: '',
    };

    constructor(props: ValidatedInputProps) {
        super(props);

        this.state = {
            hasValidText: false,
            errorMessage: '',
            value: this.props.value || '',
        };
    }

    componentDidMount() {
        if (this.props.validateOnMount) {
            this.validateInput(this.props.value);
        }
    }

    onBlur = (e: SyntheticInputEvent<*>) => {
        this.validateInput(this.state.value);
        if (this.props.onBlur) {
            this.props.onBlur(e);
        }
    };

    onChange = (e: SyntheticInputEvent<*>) => {
        this.setState({ value: e.target.value });
        if (this.props.onChange) {
            this.props.onChange(e);
        }
    };

    setErrorMessage = ({ isEmptyField = false }: { isEmptyField?: boolean } = {}) => {
        let errorMessage;
        if (isEmptyField) {
            // If input field is empty, but required
            errorMessage = this.props.requiredFieldErrorMessage;
        } else if (this.props.incorrectFieldErrorMessage) {
            // If input field is incorrect and there is a message provided
            errorMessage = this.props.incorrectFieldErrorMessage;
        } else {
            // If input field is incorrect and there is no message provided
            errorMessage = validations.errorMessages[this.props.validationType];
        }
        this.setState({ hasValidText: false, errorMessage }, () => {
            if (this.props.onError) {
                this.props.onError();
            }
        });
    };

    validateInput = (value?: string) => {
        const { validationFunction } = this.props;
        if (value && value.length) {
            const isValid = validationFunction
                ? validationFunction(value)
                : validations[this.props.validationType](value);
            if (!isValid) {
                this.setErrorMessage();
            } else {
                this.setState(
                    { hasValidText: this.props.showSuccessIcon, errorMessage: '' },
                    () => {
                        if (this.props.onSuccess) {
                            this.props.onSuccess();
                        }
                    }
                );
            }
        } else {
            if (this.props.onEmptyField) {
                this.props.onEmptyField();
            }
            if (this.props.isRequiredField) {
                this.setErrorMessage({ isEmptyField: true });
            } else {
                this.setState({ hasValidText: false, errorMessage: '' });
            }
        }
    };

    render() {
        const { placeholder, maskForPrivacy } = this.props;
        return (
            <Input
                disabled={this.props.disabled}
                dataTn={this.props.dataTn || ''}
                errorDataTn={this.props.errorDataTn}
                errorMessage={this.state.errorMessage}
                hasAnimatedPlaceholder={this.props.hasAnimatedPlaceholder && !!placeholder}
                hasValidText={this.state.hasValidText}
                label={this.props.label}
                onBlur={this.onBlur}
                onChange={this.onChange}
                placeholder={placeholder}
                value={this.state.value}
                maskForPrivacy={maskForPrivacy}
            />
        );
    }
}

export default ValidatedInput;
